import React, { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Landing() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');

  const apps = [
    { 
      title: "Quiz & Learn", 
      category: "Academic", 
      tagline: "Academic Success Starts Here", 
      desc: "Prepare for WAEC, SAT, GCSE, and national exams with AI search and timed quizzes.",
      path: "/quiz-learn-info",
      isQuizLearn: true
    },
    { 
      title: "NCLEXSmart Prep", 
      category: "Healthcare", 
      tagline: "Your Smart Path to NCLEX Success", 
      desc: "Thousands of NCLEX-style questions, NGN case studies, and detailed rationales.",
      path: "/nclexsmart-prep"
    },
    { 
      title: "PMHNP Smart Prep", 
      category: "Healthcare", 
      tagline: "Master the PMHNP Certification Exam", 
      desc: "DSM-5-TR concepts, psychopharmacology review, and clinical case scenarios.",
      path: "/pmhnp-smart-prep"
    },
    { 
      title: "CDIP Smart Prep", 
      category: "Healthcare", 
      tagline: "Clinical Documentation Integrity Made Simple", 
      desc: "AHIMA CDIP examination review, coding guidance, and documentation strategies.",
      path: "/cdip-smart-prep"
    },
    { 
      title: "Kingdom Kids Daily Devotional", 
      category: "Faith", 
      tagline: "Growing Young Hearts for Christ", 
      desc: "Ages 4–12 animated Bible stories, daily devotionals, memory verses, and quizzes.",
      path: "/kingdom-kids-daily-devotional"
    },
    { 
      title: "RiseUp 365", 
      category: "Faith", 
      tagline: "Empowering the Next Generation to Rise in Christ", 
      desc: "Teenagers ages 13–17 daily devotionals, guided prayers, and faith challenges.",
      path: "/riseup-365"
    },
    { 
      title: "Daily Grace 365", 
      category: "Faith", 
      tagline: "Your Daily Walk with God", 
      desc: "One-year Bible reading plan, reflection journal, and AI Bible assistant.",
      path: "/daily-grace-365"
    }
  ];

  const filteredApps = apps.filter(app => {
    const matchesSearch = app.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          app.tagline.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'All' || app.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="bg-slate-950 text-slate-100 min-h-screen relative overflow-hidden">
      
      {/* Hero Section with Sparkling Stars & Background Glow */}
      <section className="relative pt-32 pb-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center overflow-hidden">
        
        {/* Star Sprinkle & Sparkle Field */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
          <div className="star-sparkle top-12 left-[15%] animate-pulse" style={{animationDuration: '2s'}}></div>
          <div className="star-sparkle top-24 left-[35%]" style={{animationDelay: '0.5s', animationDuration: '3.5s'}}></div>
          <div className="star-sparkle top-16 right-[20%]" style={{animationDelay: '1.2s', animationDuration: '2.5s'}}></div>
          <div className="star-sparkle top-36 right-[35%]" style={{animationDelay: '0.8s', animationDuration: '4s'}}></div>
          <div className="star-sparkle top-20 left-[70%]" style={{animationDelay: '2s', animationDuration: '3s'}}></div>
          <div className="star-sparkle top-10 right-[10%]" style={{animationDelay: '1.5s', animationDuration: '2.2s'}}></div>
          <div className="star-sparkle top-28 left-[5%]" style={{animationDelay: '0.3s', animationDuration: '3.8s'}}></div>
          
          {/* Ambient Blue Core Glow */}
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[250px] bg-blue-600/15 blur-[100px] rounded-full pointer-events-none"></div>
        </div>

        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-950 border border-blue-800/60 text-blue-400 text-xs font-bold uppercase tracking-widest mb-6 shadow-sm">
            ✨ One Company • Multiple Solutions • Endless Possibilities
          </div>
          <h1 className="text-4xl sm:text-6xl font-black tracking-tight mb-6 leading-tight text-white">
            Empowering Minds. <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">Enriching Lives.</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-400 max-w-3xl mx-auto mb-10 leading-relaxed font-normal">
            We develop intelligent digital solutions that help students achieve academic excellence, healthcare professionals advance their careers, families nurture faith, and individuals pursue lifelong learning with confidence.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="#apps-section" 
              className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-8 py-4 rounded-2xl shadow-xl shadow-blue-500/25 transition"
            >
              Explore Ecosystem
            </a>
            <Link 
              to="https://www.assurancetechinnovation.com/login" 
              target="_blank"
              rel="noopener noreferrer"
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-8 py-4 rounded-2xl shadow-xl shadow-emerald-500/25 transition"
            >
              Enroll Now (Quiz & Learn) →
            </Link>
          </div>
        </div>
      </section>

      {/* App Portfolio & Search Section */}
      <section id="apps-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-black text-white tracking-tight">Our App Portfolio</h2>
          <p className="text-slate-400 mt-2 text-sm">Select an application below or search by topic.</p>

          <div className="mt-8 max-w-xl mx-auto">
            <input 
              type="text"
              placeholder="Search apps by keyword..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-900 border border-slate-800 rounded-2xl px-5 py-3.5 text-sm text-white placeholder-slate-500 shadow-sm focus:outline-none focus:border-blue-500"
            />
          </div>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {['All', 'Academic', 'Healthcare', 'Faith'].map((cat) => (
              <button
                key={cat}
                onClick={() => setFilterCategory(cat)}
                className={`px-5 py-2 rounded-xl text-xs font-bold transition ${
                  filterCategory === cat 
                    ? 'bg-blue-600 text-white shadow-md' 
                    : 'bg-slate-900 text-slate-400 border border-slate-800 hover:bg-slate-800'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredApps.map((app, index) => (
            <div 
              key={index} 
              className="bg-slate-900 rounded-3xl p-8 border border-slate-800/80 shadow-2xl flex flex-col justify-between hover:border-blue-500/50 transition"
            >
              <div>
                <div className="flex justify-between items-start mb-4">
                  <span className="text-[10px] font-black uppercase tracking-widest px-3 py-1 bg-blue-950 text-blue-400 rounded-full border border-blue-900/50">
                    {app.category}
                  </span>
                  
                  <div className="w-12 h-12 rounded-2xl bg-slate-950 flex items-center justify-center border border-slate-800 overflow-hidden shadow-inner">
                    <img 
                      src="/Logo.png" 
                      alt="Logo" 
                      className="w-8 h-8 object-contain"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                    />
                    <span className="text-[10px] font-black text-blue-500 hidden">APP</span>
                  </div>
                </div>

                <h3 className="text-2xl font-black text-white mb-1">{app.title}</h3>
                <p className="text-xs font-bold text-indigo-400 mb-3">{app.tagline}</p>
                <p className="text-sm text-slate-400 leading-relaxed mb-6">{app.desc}</p>
              </div>

              <div className="pt-4 border-t border-slate-800/80">
                {app.isQuizLearn ? (
                  <Link 
                    to={app.path} 
                    className="w-full text-center block bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm py-3 px-4 rounded-xl shadow-md transition"
                  >
                    View Details & Launch
                  </Link>
                ) : (
                  <Link 
                    to={app.path} 
                    className="w-full text-center block bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm py-3 px-4 rounded-xl shadow-md transition"
                  >
                    Explore App →
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}